<script
  setup
  lang="ts"
  generic="T extends Record<string, AcceptableValue>, U extends keyof T"
>
import { ToggleGroupItem, ToggleGroupRoot, type AcceptableValue } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { useVModel } from '@vueuse/core'

const props = withDefaults(
  defineProps<{
    class?: HTMLAttributes['class']
    itemClass?: HTMLAttributes['class']
    valueKey?: U
    labelKey?: U
    options?: T[]
    multiple?: boolean
    modelValue?: AcceptableValue | AcceptableValue[]
  }>(),
  {
    class: undefined,
    itemClass: undefined,
    valueKey: () => 'id' as U,
    labelKey: () => 'label' as U,
    options: undefined,
    multiple: false,
    modelValue: undefined
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value?: AcceptableValue | AcceptableValue[]): void
}>()

const vModel = useVModel(props, 'modelValue', emit)
</script>

<template>
  <ToggleGroupRoot
    v-model="vModel"
    :type="props.multiple ? 'multiple' : 'single'"
    :class="cn('flex flex-wrap gap-2', props.class)"
  >
    <ToggleGroupItem
      v-for="(item, nIndex) in options"
      :key="item[valueKey] + '-' + nIndex"
      :value="item[valueKey] || '-'"
      :disabled="!!item.disabled"
      :class="
        cn('flex rounded-full focus:outline-flux-primary', props.itemClass)
      "
    >
      <template #default="{ state }">
        <slot
          name="item"
          :data="item"
          :active="state === 'on'"
          :label="item[labelKey]?.toString()"
        >
          <UiBadge
            :label="item[labelKey]?.toString()"
            :variant="state === 'on' ? 'primaryContainer' : 'primaryOutline'"
            :icon="state === 'on' ? 'material-symbols:check' : undefined"
            size="lg"
          />
        </slot>
      </template>
    </ToggleGroupItem>
  </ToggleGroupRoot>
</template>
